// src/billing/money.js — the charge of an immediate plan change, printed from the preview's minor units (v204).
//
// `POST /billing/change-plan/preview` sends `amount` as INTEGERS in the currency's minor units (cents), straight from
// Stripe's own preview. ⛔ Printed, never computed: the one subtraction here is a CONSISTENCY CHECK that decides
// which rows to show, never a figure shown to the user. `formatPrice` (creditsFormat.js, no decimals) stays for the
// plan cards; a charge needs its cents.
import { getActiveLocale } from '../i18n/locale.js';

/** The currency's own number of decimals (2 for EUR, 0 for JPY); null for an unknown currency. */
function fractionDigits(currency) {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
  } catch {
    return null;
  }
}

/** Minor units → «36,60 €» in the active locale; null when it is not an integer amount in a known currency. */
export function formatMinorUnits(amount, currency) {
  if (!Number.isInteger(amount) || typeof currency !== 'string' || !currency) return null;
  const code = currency.toUpperCase();
  const digits = fractionDigits(code);
  if (digits == null) return null;
  return new Intl.NumberFormat(getActiveLocale(), { style: 'currency', currency: code })
    .format(amount / 10 ** digits);
}

/**
 * The rows of the confirmation's charge table, as `{ id, value }` (the component words the labels).
 * ⭐ The new plan's price and the unused part of the current plan are shown only when they ADD UP to the subtotal:
 * a coupon or a discount we do not model would otherwise print lines that do not sum. Then: subtotal, VAT, total.
 * @returns {Array<{id: string, value: string}>|null} null when the preview carries no usable amount
 */
export function chargeRows(amount) {
  if (!amount || typeof amount !== 'object') return null;
  const { currency, subtotal, tax, total, credit_for_unused_time: credit, new_plan_price: price } = amount;
  const fmt = (n) => formatMinorUnits(n, currency);
  const taxValue = fmt(tax);
  const totalValue = fmt(total);
  const subtotalValue = fmt(subtotal);
  if (taxValue == null || totalValue == null || subtotalValue == null) return null;
  const tail = [{ id: 'tax', value: taxValue }, { id: 'total', value: totalValue }];
  const parts = Number.isInteger(price) && Number.isInteger(credit) && price - credit === subtotal;
  if (!parts) return [{ id: 'subtotal', value: subtotalValue }, ...tail];
  // Nothing to credit → no «-0,00 €» row.
  const unused = credit > 0 ? [{ id: 'unused', value: fmt(-credit) }] : [];
  return [{ id: 'newPlan', value: fmt(price) }, ...unused, ...tail];
}
